// What the welcome-gift popup says, worked out from the coupons actually on the
// account — never from config alone. The popup used to say "You received 5
// bonus coupons": a stock count, not a value. Now it leads with the percentage
// the first deposit earns and shows the whole ladder behind it.
import type { DepositCoupon } from '../../features/wallet/api';

export type WelcomeStep = {
  bonusType: string;
  bonusValue: number;
  /** now = usable on the next deposit; locked = waiting for a later deposit */
  state: 'now' | 'locked';
};

export type WelcomePopupModel = {
  /** The rung the next deposit unlocks — the headline. */
  headline: WelcomeStep;
  steps: WelcomeStep[];
  /** True only when every rung is open-ended; the popup then says "no expiry". */
  noExpiry: boolean;
};

// The ladder templates are coded first_deposit_1..5; the number is the deposit
// they apply to. Anything without that shape sorts after, by template id, so a
// differently named campaign still renders in a stable order.
function rung(code: string | undefined): number {
  const m = /_(\d+)$/.exec(code || '');
  return m ? Number(m[1]) : Number.MAX_SAFE_INTEGER;
}

export function buildWelcomePopupModel(coupons: DepositCoupon[] | undefined): WelcomePopupModel | null {
  const live = (coupons || []).filter((c) => c.status === 'available' || c.status === 'locked');
  if (live.length === 0) return null;
  const ordered = [...live].sort((a, b) => {
    const byRung = rung(a.code) - rung(b.code);
    if (byRung !== 0) return byRung;
    // An available coupon is the next deposit's; locked ones come after it.
    if (a.status !== b.status) return a.status === 'available' ? -1 : 1;
    return Number(a.templateId || 0) - Number(b.templateId || 0);
  });
  const steps: WelcomeStep[] = ordered.map((c) => ({
    bonusType: String(c.bonusType || 'percent'),
    bonusValue: Number(c.bonusValue) || 0,
    state: c.status === 'available' ? 'now' : 'locked',
  }));
  // Headline is the usable rung; if none is usable yet (all locked — should not
  // happen before the first deposit, but the data decides), take the first.
  const headline = steps.find((s) => s.state === 'now') || steps[0];
  return {
    headline,
    steps,
    noExpiry: live.every((c) => c.expiresAt === null || c.expiresAt === undefined),
  };
}
